/* NICO GINGIBER START - mega menu hover-close fix
 *
 * THE BUG (confirmed live via a console diagnostic - see project memory for the full trace)
 * Opening a submenu is animated: assets/base.css sets `--submenu-animation-speed: 360ms`, and
 * blocks/_header-menu.liquid transitions the submenu's `clip-path` over that duration to reveal
 * it. The submenu's LAYOUT position/size (the `top`/height custom properties) snap to their
 * final values immediately when it opens - only the visual reveal is animated. Clipped-out
 * regions of an element don't just fail to paint, they also don't receive pointer events. So
 * for roughly the first 360ms after a submenu opens, most of its area is not actually
 * hit-testable yet, even though it's already rendered "there" geometrically.
 *
 * A shopper's cursor reaches the submenu's screen position almost immediately - there's no real
 * gap between the trigger link and the submenu. If they move toward it before the animation
 * finishes, the browser's hit-test at that point falls through to whatever is BEHIND the menu
 * (page content), which isn't a descendant of the open `<li>`. That fires a genuine
 * `pointerleave` on the `<li>`, which closes the menu - regardless of the angle of approach.
 * This is a known, still-open upstream Horizon bug, not a Gingiber regression -
 * assets/header-menu.js itself is untouched stock core.
 *
 * (A secondary, related version of the same problem: moving diagonally from one top-level link
 * toward a submenu item can cross a sibling `<li>`'s hit area - since the submenu spans the full
 * header width - and that sibling's `pointerenter` steals activation before the cursor arrives.)
 *
 * EARLIER ATTEMPTS AT THIS FILE (see project memory for full history)
 * v1 tried a capture-phase `document` listener for pointerenter/pointerleave - didn't work
 * because Horizon's own component.js registers an equivalent listener even earlier, and it runs
 * to completion (calling activate()/deactivate() directly) before this script's listener got a
 * turn. v2 switched to overriding `activate()`/`deactivate()` directly on the `<header-menu>`
 * instance - which does correctly intercept every call - but used a velocity/trajectory
 * prediction that assumed "cursor has reached the submenu's Y position" meant "safely arrived,
 * no longer needs protecting." That assumption is backwards: reaching that position while the
 * open animation hasn't finished is exactly when a spurious close happens.
 *
 * THIS VERSION
 * Same method-patching mechanism as v2 (see below), but the protection check is now primarily
 * GEOMETRIC rather than predictive: is the cursor currently within the open item's own box OR
 * within its submenu's final target rectangle (via getBoundingClientRect, which is unaffected by
 * clip-path/animation state)? If so, don't let it close, regardless of whether that exact point
 * is paintable/hit-testable yet. A secondary trajectory check still covers the "still
 * approaching, crossing a sibling on the way" case for shoppers moving fast diagonally.
 *
 * HOW THE INTERCEPTION WORKS
 * assets/component.js (Horizon's base Component class) resolves which method to call via a LIVE
 * property lookup - `instance[methodName]` - every time an `on:pointerenter="/activate"` /
 * `on:pointerleave="/deactivate"` binding fires, not a reference captured once ahead of time.
 * This script overrides `activate`/`deactivate` directly as own properties on each
 * `<header-menu>` element, wrapping the originals with the protection check above. That works
 * regardless of event listener registration order, because it changes what Horizon's dispatcher
 * finds when it looks the method up, rather than competing for the event itself.
 *
 * It does NOT edit header-menu.js or _header-menu.liquid's core markup/logic - fully additive,
 * loaded after header-menu.js, so a future Horizon theme update won't silently wipe it.
 *
 * If a close/switch is suppressed and the shopper's cursor doesn't end up in the submenu after
 * all (stops on a sibling, or moves away entirely), a grace-window fallback - deliberately set
 * longer than the submenu's own opening animation - checks where the cursor actually ended up
 * and calls the ORIGINAL activate/deactivate directly to reconcile state.
 *
 * TUNING
 * DOWNWARD_INTENT_RATIO and the animation-duration read below are the values most likely to
 * need adjustment after further live QA - see inline notes.
 */
(() => {
  /**
   * Read a CSS custom property's duration in milliseconds, defaulting if it's missing/unparsable.
   * @param {string} varName
   * @param {number} fallbackMs
   */
  function readDurationMs(varName, fallbackMs) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    if (!raw) return fallbackMs;
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return fallbackMs;
    return raw.includes('ms') ? value : value * 1000;
  }

  /** How long the submenu's own opening animation takes - read live so it stays correct if the theme's timing changes. */
  const SUBMENU_ANIMATION_MS = readDurationMs('--submenu-animation-speed', 360);

  /** How far back (ms) to look when estimating the cursor's direction of travel. */
  const POINTER_HISTORY_MS = 120;

  /**
   * How "steep" the downward movement must be, relative to sideways movement, to count as
   * "still approaching the submenu" while it hasn't been geometrically reached yet. 0.5 means
   * vertical movement must be at least half of horizontal movement. Raise this if genuine
   * sideways moves between top-level items are getting incorrectly protected; lower it if fast
   * diagonal drags into a submenu are still closing early.
   */
  const DOWNWARD_INTENT_RATIO = 0.5;

  /**
   * Grace window (ms) after suppressing a close/switch before this script checks where the
   * cursor actually ended up and reconciles state. Deliberately kept longer than the submenu's
   * own opening animation (SUBMENU_ANIMATION_MS) - reconciling before the animation has even
   * finished would reintroduce the same premature-close bug this file exists to fix.
   */
  const RECONCILE_DELAY_MS = SUBMENU_ANIMATION_MS + 150;

  /** Most recent pointer position, updated on every pointermove. */
  let lastPointer = { x: 0, y: 0 };

  /** Rolling buffer of recent {x, y, t} samples, used to estimate direction of travel. */
  let pointerHistory = [];

  /**
   * NICO GINGIBER: items that already have a reconcile check scheduled. Used instead of a single
   * shared timer - see scheduleReconcile() for why.
   * @type {WeakSet<Element>}
   */
  const pendingReconcile = new WeakSet();

  document.addEventListener(
    'pointermove',
    (event) => {
      const now = performance.now();
      lastPointer = { x: event.clientX, y: event.clientY };
      pointerHistory.push({ x: event.clientX, y: event.clientY, t: now });
      while (pointerHistory.length && now - pointerHistory[0].t > POINTER_HISTORY_MS) {
        pointerHistory.shift();
      }
    },
    { passive: true }
  );

  /**
   * Rough velocity vector over the tracked pointer history.
   */
  function getVelocity() {
    if (pointerHistory.length < 2) return { dx: 0, dy: 0 };
    const first = pointerHistory[0];
    const last = pointerHistory[pointerHistory.length - 1];
    return { dx: last.x - first.x, dy: last.y - first.y };
  }

  /**
   * Is the last known cursor position geometrically within `item`'s own box OR within its open
   * submenu's final target rectangle - regardless of whether that exact point is paintable/
   * hit-testable yet (getBoundingClientRect is unaffected by clip-path/animation state, unlike
   * elementFromPoint). This is the primary guard: it catches the case where the shopper's
   * cursor has genuinely reached the submenu but the opening animation hasn't caught up.
   * @param {Element} item
   */
  function isWithinExpandedRegion(item) {
    const submenu = item.querySelector(':scope > .menu-list__submenu[data-active]');
    if (!submenu) return false;

    const { x, y } = lastPointer;
    // NICO GINGIBER: exclusive bounds - two flush, adjacent top-level nav items share an edge
    // (item1.rect.right === item2.rect.left), so inclusive `<=`/`>=` comparisons could count the
    // cursor as "still within item1" for the single frame it sits exactly on that shared edge
    // while crossing into item2, incorrectly re-arming protection on every sibling crossing.
    const within = (rect) => x > rect.left && x < rect.right && y > rect.top && y < rect.bottom;

    const itemRect = item.getBoundingClientRect();
    if (within(itemRect)) return true;

    // NICO GINGIBER: confirmed via live measurement that the submenu element's own bounding box
    // extends well ABOVE the top-level row (e.g. submenu top=34 while the row itself sits at
    // top=128/bottom=155) - it isn't positioned strictly below the row, it's a taller panel that
    // fully contains the row's own y-range. Checking "cursor within submenu.getBoundingClientRect()"
    // alone therefore also matches hovering a completely different sibling item still inside that
    // same row, since the row is nested inside the submenu's bounding box - which is exactly what
    // was causing the menu to get stuck on the first item during a horizontal sweep. Only trust the
    // submenu's rect once the cursor has actually dropped below this item's own row.
    const submenuRect = submenu.getBoundingClientRect();
    return y > itemRect.bottom && within(submenuRect);
  }

  /**
   * Is the cursor still approaching `item`'s open submenu (hasn't geometrically reached it yet),
   * on a steep-enough downward trajectory that it's likely heading into it rather than moving
   * sideways to a different top-level item? This is the secondary guard: it catches a shopper
   * diagonally crossing a sibling `<li>` on the way down, before isWithinExpandedRegion would
   * otherwise kick in.
   * @param {Element} item
   */
  function isApproachingSubmenu(item) {
    const submenu = item.querySelector(':scope > .menu-list__submenu[data-active]');
    if (!submenu) return false;

    // NICO GINGIBER: use the open item's own row bottom, not submenu.getBoundingClientRect().top -
    // the submenu element's rect extends upward to cover the whole row (and above), so its own
    // top edge isn't a meaningful "have we visually reached the dropdown yet" boundary. See
    // isWithinExpandedRegion() for the measured rects that confirm this.
    const rowBottom = item.getBoundingClientRect().bottom;
    if (lastPointer.y >= rowBottom) return false; // already past the row - isWithinExpandedRegion covers this

    const { dx, dy } = getVelocity();
    return dy > 0 && dy > Math.abs(dx) * DOWNWARD_INTENT_RATIO;
  }

  /**
   * Should `item` be protected from closing/being switched away from right now?
   * @param {Element} item
   */
  function shouldProtect(item) {
    return isWithinExpandedRegion(item) || isApproachingSubmenu(item);
  }

  /**
   * After the grace window, check where the cursor actually ended up and reconcile state by
   * calling the ORIGINAL (unwrapped) activate/deactivate directly - close the stale item for
   * real if the cursor genuinely left it and its submenu, and let whatever is actually under
   * the cursor now activate normally.
   *
   * NICO GINGIBER: while `item` is protected, core's own `header-menu.js` keeps its normal
   * Safari hit-test reconciliation loop running (`#onPointerMove`/`#reconcilePointerTarget`) -
   * and since our wrapper is suppressing the real activate(), core's `#state.activeItem` never
   * updates, so that loop keeps re-dispatching synthetic `pointerenter` events at whatever is
   * currently under the cursor for as long as the sweep continues. Each of those re-enters this
   * wrapper's activate() and, previously, called scheduleReconcile() again - which used to
   * `clearTimeout` + reschedule a single shared timer every time, so a fast sweep across several
   * top-level items could keep pushing the grace window out indefinitely and the menu would
   * appear permanently stuck on the first item instead of settling within RECONCILE_DELAY_MS.
   * Tracking "already scheduled" per item instead lets the first timer run to completion
   * regardless of how many more suppressed attempts arrive for the same item in the meantime,
   * bounding the worst case to one RECONCILE_DELAY_MS window.
   * @param {Element} item - the item that was protected from closing/switching.
   * @param {(event: {target: Element}) => void} originalActivate
   * @param {(event: {target: Element}) => void} originalDeactivate
   * @param {(item: Element | null) => void} setActiveItem - updates the calling instance's own
   *   tracked "currently active item" once this reconcile actually resolves - see patchHeaderMenu().
   */
  function scheduleReconcile(item, originalActivate, originalDeactivate, setActiveItem) {
    if (pendingReconcile.has(item)) return; // already scheduled - let that one run its course
    pendingReconcile.add(item);

    setTimeout(() => {
      pendingReconcile.delete(item);

      if (isWithinExpandedRegion(item)) return; // still there (by position) - nothing to fix

      // Cursor settled somewhere else - close the stale item for real.
      originalDeactivate({ target: item });
      setActiveItem(null);

      // If the cursor is now resting on a different top-level item, open that one for real.
      const elementNow = document.elementFromPoint(lastPointer.x, lastPointer.y);
      const newItem = elementNow?.closest('.menu-list__list-item');
      if (newItem && newItem !== item) {
        originalActivate({ target: newItem });
        setActiveItem(newItem);
      }
    }, RECONCILE_DELAY_MS);
  }

  /**
   * Wrap a single <header-menu> instance's activate()/deactivate() with the protection check.
   * Safe to call more than once per instance - guarded so it only patches once.
   * @param {any} instance
   */
  function patchHeaderMenu(instance) {
    if (!instance || instance.__gingiberPatched) return;
    if (typeof instance.activate !== 'function' || typeof instance.deactivate !== 'function') return;
    instance.__gingiberPatched = true;

    const originalActivate = instance.activate.bind(instance);
    const originalDeactivate = instance.deactivate.bind(instance);

    /**
     * NICO GINGIBER: core's own `activate()` never clears the PREVIOUS submenu's `data-active`
     * attribute - only `deactivate()` does that. In the stock, unpatched theme this is harmless
     * because a real pointerleave on the old item and pointerenter on the new one arrive together.
     * Once this file can defer a deactivate call, that assumption breaks: a new item can finish
     * activating while the old item's own deactivate stays deferred, leaving TWO submenus
     * simultaneously marked `data-active`. Deriving "which item is active" via
     * `querySelector('[data-active]')` then always resolves to whichever one is first in the
     * menu's DOM order - silently protecting the wrong item and leaving whatever the shopper
     * actually has open completely unprotected against the original animation-lag bug. Confirmed
     * live: the first item in the menu never logged a "-> CLOSED" event at all, while whichever
     * item was genuinely open kept flapping shut the instant the cursor moved into its content.
     * Tracking our own single "active item" reference (mirroring core's own single
     * `#state.activeItem` model) avoids relying on that DOM invariant entirely.
     * @type {Element | null}
     */
    let trackedActiveItem = null;

    // Guard switching to a sibling while the currently open item should still be protected.
    instance.activate = (event) => {
      const target = event?.target;
      const item = target instanceof Element ? target.closest('.menu-list__list-item') : null;

      if (!item) {
        originalActivate(event);
        return;
      }

      if (item !== trackedActiveItem) {
        if (trackedActiveItem && shouldProtect(trackedActiveItem)) {
          scheduleReconcile(trackedActiveItem, originalActivate, originalDeactivate, (next) => {
            trackedActiveItem = next;
          });
          return;
        }

        // Proceeding to open `item` for real - force-close whatever was previously open first, so
        // at most one submenu ever carries `data-active` at a time (see note above).
        if (trackedActiveItem) {
          originalDeactivate({ target: trackedActiveItem });
        }
      }

      originalActivate(event);
      trackedActiveItem = item;
    };

    // Guard the open item's own close while it should still be protected.
    instance.deactivate = (event) => {
      const target = event?.target;
      const item = target instanceof Element ? target.closest('.menu-list__list-item') : null;

      if (!item) {
        originalDeactivate(event);
        return;
      }

      if (trackedActiveItem === item && shouldProtect(item)) {
        scheduleReconcile(item, originalActivate, originalDeactivate, (next) => {
          trackedActiveItem = next;
        });
        return;
      }

      originalDeactivate(event);
      if (trackedActiveItem === item) trackedActiveItem = null;
    };
  }

  function patchAll() {
    document.querySelectorAll('header-menu').forEach(patchHeaderMenu);
  }

  patchAll();
  document.addEventListener('DOMContentLoaded', patchAll);

  // Catch <header-menu> instances added later (theme editor re-renders, section hydration).
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.tagName === 'HEADER-MENU') patchHeaderMenu(node);
        node.querySelectorAll?.('header-menu').forEach(patchHeaderMenu);
      }
    }
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
/* NICO GINGIBER END */
